/**
 * Abandonment Tracking Utility
 * Tracks when users navigate away or close browser during critical flows
 */

import { trackError } from './error-tracking';
import { isAnalyticsDisabled } from './analytics-enabled';
import { getAnonymousId, getSessionId } from './tracking-context';
import { getRankingVariant } from './ranking-variant-store';

export type FunnelType = 'selling' | 'checkout' | 'login' | 'edit_listing' | 'shipping_settings';

type CleanupKey = `cleanup_${FunnelType}_tracking`;
type WindowWithCleanup = Window & typeof globalThis & Record<CleanupKey, (() => void) | undefined>;

/**
 * Where each funnel's own flow id goes in `data`, now that `session_id` carries
 * the analytics session instead.
 *
 * The flow id is still worth keeping -- it is what ties an abandonment to the
 * `checkout_session_id` on the events around it -- it just is not the column the
 * funnel joins on. `session_id` itself is a reserved key that
 * normalizeFunnelEventData drops from `data`, so it needs its own name here.
 */
const FLOW_ID_KEY: Record<FunnelType, string> = {
	checkout: 'checkout_session_id',
	selling: 'draft_session_id',
	login: 'login_session_id',
	// Both are tracked only while the form holds unsaved changes, so the flow is
	// the thing being edited: the listing, or the settings page itself.
	edit_listing: 'listing_id',
	shipping_settings: 'settings_flow_id',
};

interface AbandonmentTracker {
	/**
	 * The FLOW's id (`checkout_<uuid>`, a draft id), which is not the id the row
	 * is written under -- see sendAbandonmentEvent.
	 */
	flowSessionId: string;
	funnelType: FunnelType;
	currentStep: string;
	metadata: Record<string, unknown>;
	/**
	 * The URL the flow was being tracked on, captured at setup.
	 *
	 * `window.location.href` at send time is right for an unload but wrong for a
	 * client-side navigation away: by the time the teardown runs the router has
	 * already moved on, and the abandonment would be filed against the page the
	 * person went TO. Production rows show exactly that -- checkout abandonments
	 * with `/sell` and `/product/...` URLs.
	 */
	pageUrl: string;
	/**
	 * Why the current departure was recorded, or null while the flow is in view.
	 *
	 * One departure fires several events -- `visibilitychange`->hidden, then
	 * `pagehide`, then `beforeunload` -- and recording each one produced pairs of
	 * identical rows that doubled the abandoned count on every chart counting
	 * events rather than sessions. So the first one to arrive wins and the rest
	 * are ignored until the flow is visible again.
	 *
	 * Cleared on the return to `visible`, which is what makes a second, genuine
	 * departure recordable: this used to be a permanent `sent` flag, so a shopper
	 * who glanced at another tab was marked abandoned forever and the real exit
	 * that followed was never recorded at all.
	 */
	departedFor: DepartureReason | null;
}

/**
 * How the flow was left.
 *
 * `hidden` is the ambiguous one and by far the most common: the tab went to the
 * background. That is what leaving to fetch a verification code, open a banking
 * app for a card number, or pick a photo out of the camera roll looks like --
 * all three are steps the flow ITSELF requires, and all three were being
 * recorded as giving up. On production every one of the 11 login abandonments in
 * 14 days belongs to a session that then logged in successfully, and 53 of the
 * 71 sessions that published a listing also carry an abandonment.
 *
 * It is still sent at the moment of hiding rather than held back, because a
 * backgrounded tab may simply be discarded and never say anything again. What
 * changes is that returning emits `resumed`, so the pair can be netted off:
 * an abandonment is one with no `resumed` and no completion after it.
 */
export type DepartureReason = 'hidden' | 'unload' | 'navigated';

const trackers: Map<FunnelType, AbandonmentTracker> = new Map();

/** Remove the listeners a previous setup installed, if any are still attached. */
function removeListeners(funnelType: FunnelType) {
	const win = window as WindowWithCleanup;
	const cleanup = win[`cleanup_${funnelType}_tracking`];
	if (cleanup) {
		cleanup();
		win[`cleanup_${funnelType}_tracking`] = undefined;
	}
}

/**
 * Setup abandonment tracking for a funnel
 *
 * `flowSessionId` is the flow's own id. It is recorded in `data`, not used as the
 * row's session -- see sendAbandonmentEvent for why.
 */
export function setupAbandonmentTracking(
	funnelType: FunnelType,
	flowSessionId: string,
	currentStep: string,
	metadata: Record<string, unknown> = {}
) {
	// Every caller is an effect or a handler, so this runs in a browser.
	if (isAnalyticsDisabled()) return;

	// Starting a second attempt means the first one was abandoned, so record it
	// before the tracker is replaced. Checkout reaches this by going from one
	// listing's checkout page to another: the provider is not remounted, so no
	// unmount teardown fires and the first attempt would otherwise vanish.
	//
	// This also detaches the previous listeners. Overwriting the cleanup slot on
	// window used to orphan them, and they went on firing for the rest of the
	// tab's life against a tracker they no longer owned.
	endAbandonmentTracking(funnelType);

	// Store tracker state
	trackers.set(funnelType, {
		flowSessionId,
		funnelType,
		currentStep,
		metadata,
		pageUrl: window.location.href,
		departedFor: null,
	});

	const depart = (reason: DepartureReason) => {
		// A tracker is in the map only while its flow is open.
		const tracker = trackers.get(funnelType);
		if (!tracker) return;
		sendAbandonmentEvent(tracker, reason);
	};

	// Add beforeunload event listener
	const handleBeforeUnload = (_e: BeforeUnloadEvent) => depart('unload');

	/**
	 * The one listener iOS Safari can be relied on for.
	 *
	 * `beforeunload` is routinely skipped there, and a page entering the back/
	 * forward cache never fires it at all. `utils/track-funnel.ts` already flushes
	 * its own queue on `pagehide` for exactly this reason -- the two modules
	 * disagreed about how a page ends, so a funnel's last events could be sent
	 * while its abandonment was not.
	 */
	const handlePageHide = () => depart('unload');

	// Add visibility change listener (for mobile tab switching)
	const handleVisibilityChange = () => {
		if (document.visibilityState === 'hidden') {
			depart('hidden');
			return;
		}

		// Back in view. Say so, and re-arm: a departure that turned out to be a
		// detour should not silence the real one that may follow.
		const tracker = trackers.get(funnelType);
		if (!tracker) return;
		if (tracker.departedFor === 'hidden') {
			sendFunnelBeacon(tracker, 'resumed', { reason: 'visible' });
		}
		tracker.departedFor = null;
	};

	window.addEventListener('beforeunload', handleBeforeUnload);
	window.addEventListener('pagehide', handlePageHide);
	document.addEventListener('visibilitychange', handleVisibilityChange);

	// Store cleanup function
	const win = window as WindowWithCleanup;
	win[`cleanup_${funnelType}_tracking`] = () => {
		window.removeEventListener('beforeunload', handleBeforeUnload);
		window.removeEventListener('pagehide', handlePageHide);
		document.removeEventListener('visibilitychange', handleVisibilityChange);
	};
}

/**
 * Update current step and metadata for abandonment tracking
 */
export function updateAbandonmentStep(
	funnelType: FunnelType,
	currentStep: string,
	metadata: Record<string, unknown> = {}
) {
	const tracker = trackers.get(funnelType);
	if (tracker) {
		tracker.currentStep = currentStep;
		tracker.metadata = { ...tracker.metadata, ...metadata };
	}
}

/**
 * Add facts to what an abandonment would report without moving its step:
 * the rate selected now, who the shopper turned out to be once the session
 * loaded.
 */
export function updateAbandonmentMetadata(funnelType: FunnelType, metadata: Record<string, unknown>) {
	const tracker = trackers.get(funnelType);
	if (tracker) tracker.metadata = { ...tracker.metadata, ...metadata };
}

/**
 * Disable abandonment tracking (call when flow completes successfully)
 */
export function disableAbandonmentTracking(funnelType: FunnelType) {
	removeListeners(funnelType);
	trackers.delete(funnelType);
}

/**
 * The flow's UI is going away without having completed: record the abandonment
 * now, and stop tracking.
 *
 * Call this from the unmount of whatever owns the flow. Until it existed the only
 * teardown was disableAbandonmentTracking, which runs on SUCCESS, so leaving a
 * checkout by client-side navigation removed nothing: the listeners stayed
 * attached for the rest of the tab's life and fired at the eventual real unload,
 * from whatever page the person was on by then.
 *
 * Sending here rather than only unhooking is deliberate. A single-page navigation
 * out of the flow is an abandonment, and it is the most common one -- it just
 * never reaches `beforeunload`.
 */
export function endAbandonmentTracking(funnelType: FunnelType) {
	const tracker = trackers.get(funnelType);
	if (tracker) {
		sendAbandonmentEvent(tracker, 'navigated');
	}

	removeListeners(funnelType);
	trackers.delete(funnelType);
}

/**
 * Send abandonment event using navigator.sendBeacon (reliable even during page unload)
 *
 * The row is written under the ANALYTICS session id, the same one trackFunnelEvent
 * uses, because that is the column every funnel groups by.
 *
 * It used to be written under `tracker.sessionId` -- the flow's own id, a
 * `checkout_<uuid>` from getCheckoutSessionId or a draft id from
 * getDraftSessionId. Those are a different namespace, so an abandonment could
 * never join the events that led to it. On production over 7 days the checkout
 * funnel had 259 sessions with a `started` row and 208 with an `abandoned` row
 * and the two sets did not intersect at all; the selling funnel split 787 / 728
 * the same way. Since the funnel's entry milestone is "a row exists in this
 * funnel", one person leaving a checkout counted as two sessions, and every
 * conversion rate on the chart divided by a denominator inflated by ~1.8x.
 *
 * The flow id is still recorded, in `data` under the funnel's own key, so a
 * specific attempt can still be followed end to end.
 */
function sendAbandonmentEvent(tracker: AbandonmentTracker, reason: DepartureReason) {
	// Already recorded this departure; the other exit listeners are echoes of it.
	if (tracker.departedFor) return;
	tracker.departedFor = reason;

	sendFunnelBeacon(tracker, 'abandoned', { reason, ...departureDestination(reason) });
}

/**
 * Where an in-app navigation went, as a path.
 *
 * `navigated` alone read like leaving the site. It is the opposite: a link
 * inside the app, and which link is the whole story -- the sell form's
 * "Manage Discounts" took a seller to /account/discounts mid-draft, and the
 * row said only "navigated". The router has already moved the URL by the time
 * the teardown runs, so the current path is the destination.
 *
 * Path only: a query string can carry search terms or tokens, and the path is
 * what says which link was followed. An unload or a hidden tab has no
 * destination this page can see.
 */
function departureDestination(reason: DepartureReason): { to_path?: string } {
	if (reason !== 'navigated' || typeof window === 'undefined') return {};
	return { to_path: window.location.pathname };
}

/**
 * Put one row on the funnel, from a context that may be mid-teardown.
 *
 * Shared by `abandoned` and `resumed` so the two always carry the same step,
 * flow id and page -- a resume that could not be lined up against the departure
 * it cancels would not be worth sending.
 */
function sendFunnelBeacon(
	tracker: AbandonmentTracker,
	event: 'abandoned' | 'resumed',
	extra: Record<string, unknown>,
) {
	const endpoint = getEndpointForFunnel(tracker.funnelType);

	// Both ids live in browser storage, and reading storage throws outright when
	// the browser blocks site data. This runs inside a `beforeunload` handler, so
	// an exception here loses the abandonment and anything else that handler had
	// left to do. A row under the flow's id still beats no row, and session_id is
	// non-nullable.
	let sessionId = tracker.flowSessionId;
	let anonymousId = '';
	try {
		// In a browser getSessionId always answers an id; it is the read that can throw.
		sessionId = getSessionId();
		anonymousId = getAnonymousId();
	} catch {
		// Deliberately unreported: blocked storage is the browser's setting, not a
		// fault, and this runs during page teardown. Keeps the flow id and an empty
		// anonymous id.
	}

	// Format for funnel/track endpoint
	const payload = {
		events: [{
			session_id: sessionId,
			anonymous_id: anonymousId,
			// This module builds its own payload and beacons it straight at the
			// ingest route, so it does not inherit anything trackFunnelEvent adds.
			// Without this line every `abandoned` event on every funnel was
			// unlabelled -- and abandonment is precisely the signal an experiment
			// needs, since it is the arm that loses people who would tell you so.
			ranking_variant: getRankingVariant(),
			funnel: tracker.funnelType,
			event,
			data: JSON.stringify({
				step: tracker.currentStep,
				// Fallback name keeps a stray funnel type from writing a key called
				// "undefined" into the blob.
				[FLOW_ID_KEY[tracker.funnelType] ?? 'flow_session_id']: tracker.flowSessionId,
				...tracker.metadata,
				...extra,
			}),
			component: '',
			user_id: tracker.metadata.user_id || null,
			// Taken from window.location when the flow was set up.
			page_url: tracker.pageUrl,
			timestamp: Date.now(),
		}],
	};

	// Use sendBeacon for reliable tracking during page unload
	if (navigator.sendBeacon) {
		const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
		navigator.sendBeacon(endpoint, blob);
	} else {
		// Fallback for browsers without sendBeacon
		try {
			void fetch(endpoint, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(payload),
				keepalive: true, // Keep request alive during page unload
			});
		} catch (error) {
			trackError(error instanceof Error ? error : new Error(String(error)));
		}
	}
}

function getEndpointForFunnel(_funnelType: FunnelType): string {
	// All funnels now use the unified funnel tracking endpoint
	return '/api/analytics/funnel/track';
}
